import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppShell } from '../components/AppShell';
import { ledgerApi, type AccountDetail, type LedgerAccount } from '../lib/api/ledger';
import { formatVND } from '../lib/format';
import { exportSheet, fmtDateISO } from '../lib/export-generic';
import { useToast } from '../lib/use-toast';

const TYPE_LABEL: Record<string, string> = {
  asset: 'Loại 1-2 · Tài sản',
  liability: 'Loại 3 · Nợ phải trả',
  equity: 'Loại 4 · Vốn CSH',
  revenue: 'Loại 5-7 · Doanh thu',
  expense: 'Loại 6-8 · Chi phí',
  other: 'Loại 9 · Khác',
};

type Period = 'thang' | 'quy' | 'nam';

function toIsoDate(value: Date) {
  const offset = value.getTimezoneOffset() * 60_000;
  return new Date(value.getTime() - offset).toISOString().slice(0, 10);
}

function currentMonthValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function getRange(period: Period, monthValue: string) {
  const [year, selectedMonth] = monthValue.split('-').map(Number);
  const month = selectedMonth - 1;
  const startMonth = period === 'thang' ? month : period === 'quy' ? Math.floor(month / 3) * 3 : 0;
  const endMonth = period === 'thang' ? month : period === 'quy' ? startMonth + 2 : 11;
  return {
    from: toIsoDate(new Date(year, startMonth, 1)),
    to: toIsoDate(new Date(year, endMonth + 1, 0)),
    label: period === 'thang' ? `Tháng ${String(month + 1).padStart(2, '0')}/${year}` : period === 'quy' ? `Quý ${Math.floor(month / 3) + 1}/${year}` : `Năm ${year}`,
  };
}

export function LedgerPage() {
  const { toast, showToast } = useToast();
  const [accounts, setAccounts] = useState<LedgerAccount[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<AccountDetail | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>('thang');
  const [month, setMonth] = useState(currentMonthValue);
  const [search, setSearch] = useState('');

  const range = useMemo(() => getRange(period, month), [period, month]);

  const loadList = useCallback(async () => {
    setLoadingList(true); setErr(null);
    try {
      const r = await ledgerApi.accounts(range);
      setAccounts(r.items);
      setSelected((current) => current && r.items.some((item) => item.code === current) ? current : (r.items[0]?.code ?? null));
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLoadingList(false); }
  }, [range]);
  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (!selected) return;
    setLoadingDetail(true);
    ledgerApi.detail(selected, range).then(setDetail).catch((e) => setErr((e as Error).message)).finally(() => setLoadingDetail(false));
  }, [selected, range]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q ? accounts.filter((a) => a.code.includes(q) || a.name.toLowerCase().includes(q)) : accounts;
    const map = new Map<string, LedgerAccount[]>();
    for (const a of filtered) {
      (map.get(a.type) ?? map.set(a.type, []).get(a.type)!).push(a);
    }
    const order = ['asset', 'liability', 'equity', 'revenue', 'expense', 'other'];
    return order.filter((t) => map.has(t)).map((t) => ({ type: t, rows: map.get(t)! }));
  }, [accounts, search]);

  let running = detail?.summary.openingBalance ?? 0;

  return (
    <AppShell title="Sổ cái" subtitle="Danh sách TK có phát sinh + drilldown bút toán đã post">
      {err && <div className="pay-table-error">Lỗi: {err}</div>}
      <div className="pay-page-card lgr-workspace">
        <div className="lgr-workspace-head">
          <div>
            <span className="lgr-workspace-eyebrow">Sổ cái</span>
            <h2>Tra cứu phát sinh theo tài khoản</h2>
            <p>Chọn tài khoản để xem bút toán đã post và số dư luỹ kế.</p>
          </div>
          <div className="lgr-workspace-controls">
            <label className="clg-month-picker" title="Chọn tháng để tra cứu">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
              <input aria-label="Kỳ Sổ cái" type="month" value={month} onChange={(event) => setMonth(event.target.value)} />
            </label>
            <div className="lgr-period-tabs" aria-label="Loại kỳ">
              {(['thang', 'quy', 'nam'] as const).map((value) => (
                <button key={value} type="button" className={period === value ? 'active' : ''} onClick={() => setPeriod(value)}>
                  {value === 'thang' ? 'Tháng' : value === 'quy' ? 'Quý' : 'Năm'}
                </button>
              ))}
            </div>
            <button className="pay-btn-outline" type="button" onClick={() => { setMonth(currentMonthValue()); setPeriod('thang'); }}>Xoá bộ lọc</button>
          </div>
        </div>
        <div className="lgr-layout">
        <div className="lgr-left">
          <div className="lgr-left-head">
            <div className="lgr-left-title"><b>Danh sách tài khoản</b><span>Kỳ {range.label.replace('Tháng ', '')}</span></div>
            <div className="lgr-search"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg><input placeholder="Tìm mã hoặc tên TK..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
          </div>
          <div className="lgr-left-body">
            {loadingList ? <div className="pay-empty">Đang tải…</div> : grouped.length === 0 ? (
              <div className="pay-empty">Chưa có TK có phát sinh</div>
            ) : grouped.map((g) => (
              <div key={g.type} className="lgr-group">
                <div className="lgr-group-label">{TYPE_LABEL[g.type] ?? g.type}</div>
                {g.rows.map((a) => (
                  <button
                    key={a.code}
                    type="button"
                    className={`lgr-acc ${selected === a.code ? 'active' : ''}`}
                    onClick={() => setSelected(a.code)}
                  >
                    <span className="lgr-acc-code">{a.code}</span>
                    <span className="lgr-acc-name">{a.name}</span>
                    <span className={`lgr-acc-bal ${a.balance < 0 ? 'negative' : ''}`}>{formatVND(a.balance)}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="lgr-right">
          {detail?.account ? (
            <>
              <div className="lgr-right-head">
                <div>
                  <h2>
                    <span className="coa-code-pill parent">{detail.account.code}</span>
                    {' — '}{detail.account.name}
                  </h2>
                  <p>{TYPE_LABEL[detail.account.type] ?? detail.account.type} · Số dư dạng {detail.account.defaultBalance === 'debit' ? 'Nợ' : 'Có'}</p>
                </div>
                <div className="lgr-right-tools">
                  <button className="pay-btn-outline" type="button" onClick={() => window.print()}>In</button>
                  <button className="pay-btn-outline" type="button" onClick={() => {
                    if (!detail?.account) return;
                    exportSheet(`ledger-${detail.account.code}`, detail.entries, [
                      { header: 'Ngày CT', key: 'date', width: 12, format: fmtDateISO },
                      { header: 'Số CT', key: 'code', width: 20 },
                      { header: 'Diễn giải', key: 'description', width: 44 },
                      { header: 'TK ĐƯ', key: 'offsetAccount', width: 12 },
                      { header: 'Nợ', key: 'debit', width: 18 },
                      { header: 'Có', key: 'credit', width: 18 },
                    ], `SoCai-${detail.account.code}`);
                    showToast(`Đã xuất Sổ cái TK ${detail.account.code}`);
                  }}>Excel</button>
                </div>
              </div>

              <div className="lgr-summary">
                <div><span>Dư đầu kỳ</span><b>{formatVND(detail.summary.openingBalance)}</b></div>
                <div><span>PS Nợ</span><b style={{ color: '#1e3a8a' }}>{formatVND(detail.summary.psDebit)}</b></div>
                <div><span>PS Có</span><b style={{ color: '#9d174d' }}>{formatVND(detail.summary.psCredit)}</b></div>
                <div><span>Dư cuối</span><b>{formatVND(detail.summary.closingBalance)}</b></div>
              </div>

              <div className="lgr-note">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
                {detail.summary.count} bút toán đã post · kỳ {range.label} · chỉ hiện bút toán đã duyệt.
              </div>

              {loadingDetail ? <div className="pay-empty">Đang tải…</div> : detail.entries.length === 0 ? (
                <div className="lgr-empty">Chưa có bút toán trong kỳ.</div>
              ) : (
                <div className="pay-table-wrap"><table className="pay-tbl lgr-tbl">
                  <thead>
                    <tr>
                      <th>Ngày CT</th>
                      <th>Số CT</th>
                      <th>Diễn giải</th>
                      <th>TK ĐƯ</th>
                      <th style={{ textAlign: 'right' }}>Nợ</th>
                      <th style={{ textAlign: 'right' }}>Có</th>
                      <th style={{ textAlign: 'right' }}>Dư luỹ kế</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="lgr-opening-row"><td colSpan={6}>SỐ DƯ ĐẦU KỲ · {new Date(`${range.from}T00:00:00`).toLocaleDateString('vi-VN')}</td><td style={{ textAlign: 'right' }}>{formatVND(detail.summary.openingBalance)}</td></tr>
                    {detail.entries.map((e, i) => {
                      running = running + e.debit - e.credit;
                      return (
                        <tr key={i}>
                          <td>{new Date(e.date).toLocaleDateString('vi-VN')}</td>
                          <td><button className="pay-code-btn" type="button" onClick={() => showToast(`Xem ${e.code}`)}>{e.code}</button></td>
                          <td>{e.description}</td>
                          <td><span className="coa-code-pill">{e.offsetAccount}</span></td>
                          <td style={{ textAlign: 'right', color: e.debit ? '#1e3a8a' : '#cbd5e1' }}>{e.debit ? formatVND(e.debit) : '—'}</td>
                          <td style={{ textAlign: 'right', color: e.credit ? '#9d174d' : '#cbd5e1' }}>{e.credit ? formatVND(e.credit) : '—'}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{formatVND(running)}</td>
                        </tr>
                      );
                    })}
                    <tr className="lgr-total-row">
                      <td colSpan={4}>Cộng phát sinh</td>
                      <td style={{ textAlign: 'right', color: '#1e3a8a' }}>{formatVND(detail.summary.psDebit)}</td>
                      <td style={{ textAlign: 'right', color: '#9d174d' }}>{formatVND(detail.summary.psCredit)}</td>
                      <td />
                    </tr>
                  </tbody>
                </table></div>
              )}
            </>
          ) : (
            <div className="lgr-empty">Chọn TK bên trái để xem chi tiết bút toán.</div>
          )}
        </div>
        </div>
      </div>

      {toast && <div className="coa-toast">{toast}</div>}
    </AppShell>
  );
}
